import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import offerPageURL from 'lib/offer/offerPageURL'
import { escapeContextLabel } from 'tripPlanner/utils/contextTag'
import { buildOfferContext, readCurrencyCode } from 'tripPlanner/utils/offerContext'
import type { HotelRoomOption } from 'tripPlanner/types/hotelRoomOption'

// ── Types ──

export interface MessageEnrichmentParams {
  message: string
  multiChoiceSelection: Array<string>
  sendMessageOptions?: { offerId?: string; offerType?: string; roomOption?: HotelRoomOption }
  mobileSelectionText: string | undefined
  offerContext: { propertyName: string; contextString: string } | undefined
}

// ── Offer helpers ──

export function getOfferPropertyName(offer: App.AnyOffer): string {
  if (isBedbankOffer(offer)) {
    return offer.property.name
  }
  if (isLEHotel(offer)) {
    return offer.property?.name || offer.name
  }
  return offer.name
}

export function extractRoomPackages(
  offer: App.AnyOffer,
  currentCurrency: string,
): Array<{ name: string; price?: number; currencyCode?: string }> {
  if (isBedbankOffer(offer)) {
    return offer.packages.slice(0, 3).map((pkg) => ({
      name: pkg.name || 'Room Package',
      price: pkg.sell?.price,
      currencyCode: readCurrencyCode(pkg.sell) ?? currentCurrency,
    }))
  }

  if (isLEHotel(offer)) {
    return offer.packages.slice(0, 3).map((pkg) => ({
      name: pkg.roomType?.name || pkg.name || 'Room Package',
      price: pkg.price,
      currencyCode: readCurrencyCode(pkg) ?? currentCurrency,
    }))
  }

  return []
}

function getOfferDescription(offer: App.AnyOffer): string {
  if (isBedbankOffer(offer)) {
    return ('description' in offer && offer.description) || offer.tagline || ''
  }
  if (isLEHotel(offer)) {
    return offer.description || ''
  }
  return ''
}

function getOfferFinePrint(offer: App.AnyOffer): string | undefined {
  if (isLEHotel(offer)) {
    return offer.finePrint
  }
  if (isBedbankOffer(offer)) {
    return offer.property?.finePrintOverride
  }
  return undefined
}

export function buildOfferContextString(
  offer: App.AnyOffer,
  currentCurrency: string,
): { propertyName: string; contextString: string } {
  const propertyName = getOfferPropertyName(offer)
  const description = getOfferDescription(offer)
  const roomPackages = extractRoomPackages(offer, currentCurrency)
  const finePrint = getOfferFinePrint(offer)
  const offerUrl = offerPageURL(offer)

  const contextString = buildOfferContext(
    offer,
    propertyName,
    description,
    roomPackages,
    finePrint,
    offerUrl,
    currentCurrency,
  )

  return { propertyName, contextString }
}

// ── Message composition ──

export function buildEnrichedMessage(params: MessageEnrichmentParams): string {
  const {
    message,
    multiChoiceSelection,
    sendMessageOptions,
    mobileSelectionText,
    offerContext,
  } = params

  // Combine multi-choice selection with user's typed message
  const selectionPart = multiChoiceSelection.length > 0
    ? multiChoiceSelection.join(', ')
    : ''
  let combined = selectionPart && message.trim()
    ? `${selectionPart}, ${message}`
    : selectionPart || message

  // Append LE offer ID when adding an offer to the trip
  if (sendMessageOptions?.offerId) {
    combined = `${combined}${buildOfferIdContext(sendMessageOptions.offerId, sendMessageOptions.offerType)}`
  }

  // Append room/package selection when a specific room option was chosen
  if (sendMessageOptions?.roomOption) {
    combined = `${combined}${buildRoomOptionContext(sendMessageOptions.roomOption)}`
  }

  // Append mobile selection context
  if (mobileSelectionText) {
    combined = `${combined}\n<Context label="${escapeContextLabel(mobileSelectionText)}">The user highlighted the following text: ${mobileSelectionText}</Context>`
  }

  // Append offer context
  if (offerContext) {
    combined = `${combined}\n\n<Context label="${escapeContextLabel(offerContext.propertyName)}">${offerContext.contextString}</Context>`
  }

  return combined
}

export function buildOfferIdContext(offerId: string, offerType?: string): string {
  const offerTypeLine = offerType ? `\nOffer Type: ${offerType}` : ''
  const contextLabel = offerType === 'experience' ? 'LE Experience offer' : 'LE Hotel offer'
  return `\n<Context label="${contextLabel}">Offer ID: ${offerId}${offerTypeLine}</Context>`
}

export function buildRoomOptionContext(roomOption: HotelRoomOption): string {
  const lines: Array<string> = []
  if (roomOption.checkIn) lines.push(`Check-in: ${roomOption.checkIn}`)
  if (roomOption.checkOut) lines.push(`Check-out: ${roomOption.checkOut}`)
  if (roomOption.packageId) lines.push(`Package ID: ${roomOption.packageId}`)
  if (roomOption.packageName) lines.push(`Package Name: ${roomOption.packageName}`)
  if (roomOption.durationInDays !== undefined) lines.push(`Package Duration: ${roomOption.durationInDays}`)
  if (roomOption.roomTypeId) lines.push(`Room Type ID: ${roomOption.roomTypeId}`)
  if (roomOption.roomTypeName) lines.push(`Room Type Name: ${roomOption.roomTypeName}`)
  if (roomOption.rateId) lines.push(`Room Rate ID: ${roomOption.rateId}`)
  if (roomOption.occupancies?.length) {
    lines.push(`Rooms: ${roomOption.occupancies.length}`)
    roomOption.occupancies.forEach((room, i) => {
      const parts: Array<string> = [`${room.adults} adult${room.adults !== 1 ? 's' : ''}`]
      if (room.teenagers) parts.push(`${room.teenagers} teenager${room.teenagers !== 1 ? 's' : ''}`)
      if (room.children) parts.push(`${room.children} child${room.children !== 1 ? 'ren' : ''}`)
      if (room.infants) parts.push(`${room.infants} infant${room.infants !== 1 ? 's' : ''}`)
      lines.push(`Room ${i + 1}: ${parts.join(', ')}`)
    })
  }
  return `\n<Context label="LE Hotel room option">${lines.join('\n')}</Context>`
}
